import { useFormik } from "formik";
import * as Yup from "yup";
import { isConcessionRole, userMore } from "../model/userConcession";

const useFormUser = (
  onSubmit,
  afterSubmit,
  initialValues,
  isEditMode,
  { roles = [], concessions = [], loadingConcessions = false, errorConcessions = null } = {}
) => {
  const requiresConcession = (roleId) =>
    isConcessionRole(roles.find((role) => String(role.id) === String(roleId)));
  const validationSchema = Yup.object({
    username: Yup.string()
      .required("Nombre de usuario es requerido")
      .min(3, "Mínimo 3 caracteres")
      .max(50, "Máximo 50 caracteres"),
    email: Yup.string()
      .email("Correo inválido")
      .required("Correo es requerido")
      .max(100, "Máximo 100 caracteres"),
    password: isEditMode
      ? Yup.string() // En edición, la contraseña no es requerida en el formulario principal
      : Yup.string()
          .required("Contraseña es requerida")
          .min(6, "Mínimo 6 caracteres")
          .max(50, "Máximo 50 caracteres"),
    roleId: Yup.number()
      .required("Rol es requerido")
      .typeError("Debe seleccionar un rol válido")
      .positive("Rol inválido"),
    concessionCode: Yup.string().when("roleId", {
      is: requiresConcession,
      then: (schema) =>
        schema
          .required("Seleccione una concesión")
          .test(
            "available-concession",
            "Seleccione una concesión disponible en la lista",
            (code) =>
              !loadingConcessions &&
              !errorConcessions &&
              concessions.some((row) => row.code === code)
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    status: Yup.number().oneOf([0, 1], "Estado inválido").default(1),
  });

  // Asegurar valores iniciales completos
  const safeInitialValues = {
    username: "",
    email: "",
    password: "",
    roleId: "",
    status: 1,
    ...initialValues,
    concessionCode: String(userMore(initialValues?.more).code ?? ""),
  };

  // Limpiar valores undefined o null y convertir roleId a número si existe
  const cleanedInitialValues = Object.keys(safeInitialValues).reduce((acc, key) => {
    if (key === "roleId" && safeInitialValues[key]) {
      acc[key] = Number(safeInitialValues[key]);
    } else {
      acc[key] = safeInitialValues[key] ?? "";
    }
    return acc;
  }, {});

  const formik = useFormik({
    initialValues: cleanedInitialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError, setStatus }) => {
      setStatus(null);
      try {
        // Preparar datos para enviar
        const { concessionCode, ...submitValues } = values;
        if (requiresConcession(values.roleId)) {
          const concession = concessions.find((row) => row.code === concessionCode);
          if (loadingConcessions || errorConcessions || !concession) {
            setFieldError("concessionCode", "Seleccione una concesión disponible en la lista");
            return;
          }
          submitValues.more = { ...userMore(values.more), code: concession.code };
        } else if (requiresConcession(initialValues?.roleId)) {
          const more = userMore(values.more);
          delete more.code;
          submitValues.more = more;
        }

        // Si es modo edición y no hay contraseña, la eliminamos
        if (isEditMode && !submitValues.password) {
          delete submitValues.password;
        }

        // Asegurar que roleId sea número y exista
        if (submitValues.roleId) {
          submitValues.roleId = Number(submitValues.roleId);
        } else {
          setFieldError("roleId", "Debe seleccionar un rol");
          return;
        }

        await onSubmit(submitValues);
        afterSubmit();
      } catch (error) {
        // Manejar errores de validación del backend
        if (error.response?.data?.errors) {
          Object.keys(error.response.data.errors).forEach((key) => {
            setFieldError(key, error.response.data.errors[key]);
          });
        } else {
          setStatus(error.message || "Error al guardar el usuario");
        }
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  return formik;
};

export default useFormUser;
